import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { chooseWorkspace, loadWorkspaceEntries } from "../../core/ops/workspace-ops";
import { failedLoad, loaded, loading } from "../../core/primitives/load-status";
import { type WorkspaceOption, workspaceOptions } from "../../core/primitives/workspace";
import { ChooseWorkspaceActions, ChooseWorkspaceState } from "./choose-workspace-model";

export type ChooseWorkspaceDeps = Pick<
  AppDeps,
  | "client"
  | "authHandler"
  | "storeRepository"
  | "reviewQueueRepository"
  | "customersRepository"
  | "pendingCartsRepository"
>;

/** Every shop × location the account can work at; picking one is what opens the app. */
export class ChooseWorkspaceController extends SubtreeController implements ChooseWorkspaceActions {
  readonly state = new ChooseWorkspaceState();

  constructor(readonly deps: ChooseWorkspaceDeps) {
    super();
    this.subtree.put(ChooseWorkspaceState, this.state);
    this.subtree.put(ChooseWorkspaceActions, this);
    void this.#load();
  }

  choose = (option: WorkspaceOption) => {
    chooseWorkspace({ ...this.deps, workspace: { storeId: option.store.id, locationId: option.location?.id ?? null } });
  };

  retry = () => {
    void this.#load();
  };

  async #load(): Promise<void> {
    this.state.status.value = loading;
    const r = await loadWorkspaceEntries({ ...this.deps, stores: this.deps.storeRepository.stores });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.options.value = workspaceOptions(r.value);
    this.state.status.value = loaded;
  }
}
